import { apiClient } from './api';

/**
 * Inventory API service — focused on the admin Stock Management screens.
 * Mirrors the backend routes under /api/v1/inventory.
 */

export interface ExpiringLot {
  id: number;
  lot_id: number;
  sku_code: string;
  product_name?: string;
  lot_number: string;
  quantity_on_hand?: number;
  received_date?: string;
  location_code?: string;
  expiration_date: string;
  days_until_expiration: number;
  is_critical: boolean;
}

export async function listExpiringLots(): Promise<ExpiringLot[]> {
  const raw = await apiClient.get<{ data: ExpiringLot[]; count: number }>('/inventory/expiring-lots');
  return raw.data ?? (raw as any);
}

export async function markLotExpired(lotId: number): Promise<{ id: number; sku_id: number; lot_number: string }> {
  return apiClient.post(`/inventory/lots/${lotId}/mark-expired`);
}

// ─── Inventory Holds ────────────────────────────────────────────────────

export interface InventoryHold {
  id: number;
  sku_id: number;
  sku_code: string;
  product_name: string | null;
  lot_id: number | null;
  lot_number: string | null;
  quantity: number;
  reason: string;
  notes: string | null;
  status: 'active' | 'released';
  created_by_user_id: number;
  created_by_name: string | null;
  released_by_user_id: number | null;
  released_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateHoldInput {
  sku_id: number;
  lot_id?: number | null;
  quantity: number;
  reason: string;
  notes?: string;
}

export async function listInventoryHolds(status?: 'active' | 'released'): Promise<InventoryHold[]> {
  const raw = await apiClient.get<{ data: InventoryHold[]; count: number }>(
    '/inventory/holds',
    status ? { status } : undefined
  );
  return raw.data ?? (raw as any);
}

export async function createInventoryHold(input: CreateHoldInput): Promise<InventoryHold> {
  return apiClient.post<InventoryHold>('/inventory/holds', input);
}

export async function releaseInventoryHold(id: number): Promise<InventoryHold> {
  return apiClient.patch<InventoryHold>(`/inventory/holds/${id}/release`);
}

// ─── Inventory view (lots) ──────────────────────────────────────────────

export interface InventoryLotView {
  id: number;
  sku_id: number;
  lot_number: string;
  quantity_on_hand: number;
  received_date: string;
  expiration_date: string;
  location_code: string;
  sku_code: string;
  product_name: string;
}

/** Lots for a SKU (or all), used by the adjustment form to pick a lot. */
export async function getInventoryView(sku_id?: number, location_code?: string): Promise<InventoryLotView[]> {
  const raw = await apiClient.get<{ lots: InventoryLotView[]; count: number }>('/inventory/view', {
    ...(sku_id ? { sku_id } : {}),
    ...(location_code ? { location_code } : {}),
  });
  // The view endpoint returns { lots, count }.
  return (raw as any).lots ?? (raw as any).data ?? (raw as any);
}

// ─── Adjustments ────────────────────────────────────────────────────────

export type AdjustmentReasonCode = 'SHRINKAGE' | 'DAMAGE' | 'MISCOUNT' | 'SAMPLE' | 'CORRECTION' | 'DESTROYED';

export interface AdjustmentRow {
  id: number;
  lot_id: number;
  quantity_change: number;
  reason_code: string;
  created_at: string;
  lot_number: string;
  location_code: string;
  sku_id: number;
  sku_code: string;
  product_name: string;
  recorded_by_name: string | null;
}

export async function adjustLot(
  lotId: number,
  quantity_change: number,
  reason_code: AdjustmentReasonCode
): Promise<{ lot_id: number; new_quantity_on_hand: number }> {
  return apiClient.patch(`/inventory/lots/${lotId}/adjust`, { quantity_change, reason_code });
}

export async function listAdjustments(limit = 50): Promise<AdjustmentRow[]> {
  const raw = await apiClient.get<{ data: AdjustmentRow[]; count: number }>('/inventory/adjustments', { limit });
  return raw.data ?? (raw as any);
}

// ─── Per-SKU summary (available / reserved / on-hand / locations) ─────────

export interface InventorySummaryRow {
  sku_id: number;
  on_hand: number;
  reserved: number;
  available: number;
  location_count: number;
  locations: string[];
  earliest_expiration: string | null;
}

export async function getInventorySummary(): Promise<InventorySummaryRow[]> {
  const raw = await apiClient.get<{ data: InventorySummaryRow[]; count: number }>('/inventory/summary');
  return raw.data ?? (raw as any);
}
